import { useState, type FormEvent } from 'react';
import { Button, Card, Field } from '@crave/ui';

export interface FeedbackPizza {
  /** The order line, so two of the same pizza can be rated apart. */
  id: string;
  name: string;
  quantity: number;
}

export interface FeedbackInput {
  overall?: number;
  crust?: number;
  sauce?: number;
  toppings?: number;
  would_order?: 'YES' | 'MAYBE' | 'NO';
  comment?: string;
  first_name?: string;
  contact_email?: string;
  items?: { order_item_id: string; overall: number }[];
}

function Stars({
  label,
  value,
  onChange,
  optional,
}: {
  label: string;
  value: number | null;
  onChange: (n: number) => void;
  optional?: boolean;
}) {
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <span style={{ fontWeight: 600 }}>
        {label}
        {optional && <span style={{ color: 'var(--ink-soft)', fontWeight: 400 }}> (optional)</span>}
      </span>
      <div role="radiogroup" aria-label={label} style={{ display: 'flex', gap: 6 }}>
        {[1, 2, 3, 4, 5].map((n) => {
          const on = value !== null && n <= value;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={value === n}
              aria-label={`${n} out of 5`}
              onClick={() => onChange(n)}
              style={{
                width: 44,
                height: 44,
                borderRadius: 8,
                border: `2px solid ${on ? 'var(--crave-red)' : 'var(--line)'}`,
                background: on ? 'var(--crave-red)' : 'var(--paper)',
                color: on ? '#fff' : 'var(--line)',
                fontSize: 20,
                cursor: 'pointer',
              }}
            >
              ★
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function FeedbackForm({
  title,
  askEmail,
  askName,
  pizzas,
  emailHint = 'Only if you want to hear when this pizza hits the menu.',
  onSubmit,
}: {
  title: string;
  askEmail?: boolean;
  /** A first name, so a tasting response has a face on it. Never required. */
  askName?: boolean;
  /**
   * The pizzas on the order, each rated on its own. With pizzas, those stars are the rating and the
   * order-level score is their average; one pizza asks one question, as it always did.
   */
  pizzas?: FeedbackPizza[];
  /** Why we are asking, which is not the same on a tasting sample and on the feedback link. */
  emailHint?: string;
  onSubmit: (body: FeedbackInput) => Promise<void>;
}) {
  const [overall, setOverall] = useState<number | null>(null);
  const [crust, setCrust] = useState<number | null>(null);
  const [sauce, setSauce] = useState<number | null>(null);
  const [toppings, setToppings] = useState<number | null>(null);
  const [would, setWould] = useState<'YES' | 'MAYBE' | 'NO' | null>(null);
  const [comment, setComment] = useState('');
  const [perPizza, setPerPizza] = useState<Record<string, number>>({});
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const rated = Object.entries(perPizza);
    // With several pizzas the stars live on each one, so an order-level score is not asked for.
    if (overall === null && rated.length === 0) {
      setError(
        pizzas?.length ? 'Tap the stars for at least one pizza.' : 'Tap a star rating first.',
      );
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        ...(overall !== null ? { overall } : {}),
        ...(rated.length > 0
          ? { items: rated.map(([id, n]) => ({ order_item_id: id, overall: n })) }
          : {}),
        ...(crust !== null ? { crust } : {}),
        ...(sauce !== null ? { sauce } : {}),
        ...(toppings !== null ? { toppings } : {}),
        ...(would !== null ? { would_order: would } : {}),
        ...(comment.trim() ? { comment: comment.trim() } : {}),
        ...(askName && name.trim() ? { first_name: name.trim() } : {}),
        ...(askEmail && email.trim() ? { contact_email: email.trim() } : {}),
      });
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send that. Try again.');
    } finally {
      setBusy(false);
    }
  }

  if (done)
    return (
      <Card>
        <strong>Thank you.</strong>
        <p style={{ margin: '6px 0 0', color: 'var(--ink-soft)' }}>
          That goes straight to the people who made it.
        </p>
      </Card>
    );

  return (
    <Card>
      <form onSubmit={submit} style={{ display: 'grid', gap: 16 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>{title}</h2>
        {pizzas && pizzas.length > 0 ? (
          pizzas.map((p) => (
            <Stars
              key={p.id}
              label={p.name}
              value={perPizza[p.id] ?? null}
              onChange={(n) => setPerPizza((prev) => ({ ...prev, [p.id]: n }))}
            />
          ))
        ) : (
          <Stars label="Overall" value={overall} onChange={setOverall} />
        )}
        {(pizzas?.length ?? 0) < 2 && (
          <>
            <Stars label="Crust" value={crust} onChange={setCrust} optional />
            <Stars label="Sauce" value={sauce} onChange={setSauce} optional />
            <Stars label="Toppings" value={toppings} onChange={setToppings} optional />
          </>
        )}
        <div style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontWeight: 600 }}>Would you order it again?</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['YES', 'MAYBE', 'NO'] as const).map((w) => (
              <button
                key={w}
                type="button"
                aria-pressed={would === w}
                onClick={() => setWould(w)}
                style={{
                  minHeight: 44,
                  padding: '0 16px',
                  borderRadius: 8,
                  border: `2px solid ${would === w ? 'var(--ink)' : 'var(--line)'}`,
                  background: would === w ? 'var(--ink)' : 'var(--paper)',
                  color: would === w ? '#fff' : 'var(--ink)',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {w === 'YES' ? 'Yes' : w === 'MAYBE' ? 'Maybe' : 'No'}
              </button>
            ))}
          </div>
        </div>
        <label style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontWeight: 600 }}>
            Anything else?{' '}
            <span style={{ color: 'var(--ink-soft)', fontWeight: 400 }}>(optional)</span>
          </span>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            maxLength={1000}
            style={{
              padding: 10,
              borderRadius: 8,
              border: '2px solid var(--line)',
              font: 'inherit',
            }}
          />
        </label>
        {askName && (
          <Field
            label="First name (optional)"
            value={name}
            maxLength={60}
            autoComplete="given-name"
            onChange={(e) => setName(e.target.value)}
          />
        )}
        {askEmail && (
          <Field
            label="Email (optional)"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            hint={emailHint}
          />
        )}
        {error && <p style={{ margin: 0, color: 'var(--crave-red)' }}>{error}</p>}
        <Button type="submit" disabled={busy}>
          {busy ? 'Sending…' : 'Send feedback'}
        </Button>
      </form>
    </Card>
  );
}
